import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import PillHero from "@/components/pill-page/PillHero";
import PillReviewTicker from "@/components/pill-page/PillReviewTicker";
import PillTrustBar from "@/components/pill-page/PillTrustBar";
import PillPageSEO from "@/components/pill-page/PillPageSEO";
import DirectAnswer from "@/components/common/DirectAnswer";
import Sources, { type SourceRef } from "@/components/common/Sources";

const PILL_PAGE_SOURCES: SourceRef[] = [
  {
    text: "NHS. Combined pill and progestogen-only pill: how effective is it?",
    url: "https://www.nhs.uk/conditions/contraception/",
  },
  {
    text: "NHS England. NHS Pharmacy Contraception Service.",
    url: "https://www.england.nhs.uk/primary-care/pharmacy/pharmacy-services/nhs-pharmacy-contraception-service/",
  },
];

const PillHowItWorks = lazy(() => import("@/components/pill-page/PillHowItWorks"));
const PillComparison = lazy(() => import("@/components/pill-page/PillComparison"));
const PillTestimonials = lazy(() => import("@/components/pill-page/PillTestimonials"));
const PillEligibility = lazy(() => import("@/components/pill-page/PillEligibility"));
const PillTypes = lazy(() => import("@/components/pill-page/PillTypes"));
const PillSafetyConsultation = lazy(() => import("@/components/pill-page/PillSafetyConsultation"));
const PillFAQ = lazy(() => import("@/components/pill-page/PillFAQ"));
const PillFinalCTA = lazy(() => import("@/components/pill-page/PillFinalCTA"));
const Footer = lazy(() => import("@/components/landing/Footer"));
const StartNowFab = lazy(() => import("@/components/landing/StartNowFab"));

const SectionFallback = () => <div className="min-h-[200px]" />;

const GetThePillOnline = () => {
  return (
    <>
      <PillPageSEO />
      <Navbar />
      <main className="min-h-screen bg-background pt-20">
        <PillReviewTicker />
        <PillHero />
        <PillTrustBar />

        {/* Direct answer, concise summary for readers and AI overviews */}
        <div className="px-6 pt-8">
          <div className="max-w-2xl mx-auto">
            <DirectAnswer
              paragraphs={[
                "Contraception Direct is a free NHS service that delivers the contraceptive pill (also called birth control) to your door. Book a short phone consultation with a GPhC-registered pharmacist, and if the pill is right for you it arrives in discreet, plain packaging within 1 to 3 working days.",
                "There is no GP appointment and no prescription needed. The consultation, the pill, and delivery are all free.",
              ]}
            />
          </div>
        </div>

        {/* In brief, source-ready summary for AI extraction / citation */}
        <div className="px-6 pt-4">
          <div className="max-w-2xl mx-auto rounded-2xl border border-border/50 bg-card p-5">
            <h2 className="text-sm font-bold text-foreground mb-3">In brief</h2>
            <dl className="space-y-1.5 text-[0.9rem] leading-[1.6] text-foreground/75">
              {[
                ["Service", "NHS Pharmacy Contraception Service: free pill, consultation and delivery."],
                ["Suitable for", "eligible UK patients starting, restarting, switching, or continuing the pill."],
                ["Consultation", "a short phone call with a GPhC-registered pharmacist; supply depends on their assessment."],
                ["Cost", "free, with no prescription charge and no card details."],
                ["Delivery", "discreet UK-wide postal delivery within 1 to 3 working days."],
                ["Alternatives", "a GP, sexual health clinic, or local pharmacy."],
              ].map(([label, value]) => (
                <div key={label} className="flex gap-2">
                  <dt className="font-semibold text-foreground/90 shrink-0">{label}:</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-3 text-[0.85rem] text-foreground/70">
              Order by pill type:{" "}
              <Link to="/get-the-pill-online/mini-pill/" className="text-primary underline underline-offset-2">the mini pill</Link>
              {" "}or{" "}
              <Link to="/get-the-pill-online/combined-pill/" className="text-primary underline underline-offset-2">the combined pill</Link>.
              {" "}Already on the pill?{" "}
              <Link to="/get-the-pill-online/repeat-prescription/" className="text-primary underline underline-offset-2">Get a repeat supply</Link>.
            </p>
          </div>
        </div>

        {/* Answers "buy / order the pill online" searches: you can, but you do not need to pay */}
        <section className="px-6 pt-8" aria-labelledby="buy-online">
          <div className="max-w-2xl mx-auto space-y-3 text-[0.95rem] leading-[1.7] text-foreground/80">
            <h2 id="buy-online" className="text-xl font-bold text-foreground">Can you buy the contraceptive pill online?</h2>
            <p>
              You can buy the contraceptive pill online from private online pharmacies, but in the UK you do not need to. The NHS Pharmacy Contraception Service supplies the combined pill and the mini pill free after a consultation with a pharmacist, and some pharmacies, including ours, provide it online.
            </p>
            <p>
              Private services charge for each pack, and some charge for the consultation too. Some mini pills can also be bought over the counter in a pharmacy, which you pay for. With us, the consultation, the pill and delivery are all free, and you never enter card details.
            </p>
            <p>
              Compare the routes in{" "}
              <Link to="/guides/are-you-overpaying-for-your-pill/" className="text-primary underline underline-offset-2">are you overpaying for your pill?</Link>
              {" "}or read{" "}
              <Link to="/guides/is-the-contraceptive-pill-free/" className="text-primary underline underline-offset-2">is the contraceptive pill free?</Link>
            </p>
          </div>
        </section>

        <Suspense fallback={<SectionFallback />}>
          <PillHowItWorks />
          <div className="h-px bg-border/50 mx-6 lg:mx-auto lg:max-w-5xl" />
          <PillComparison />
          <div className="h-px bg-border/50 mx-6 lg:mx-auto lg:max-w-5xl" />
          <PillTestimonials />
          <div className="h-px bg-border/50 mx-6 lg:mx-auto lg:max-w-5xl" />
          <PillEligibility />
          <div className="h-px bg-border/50 mx-6 lg:mx-auto lg:max-w-5xl" />
          <PillTypes />
          <div className="h-px bg-border/50 mx-6 lg:mx-auto lg:max-w-5xl" />
          <PillSafetyConsultation />
          <div className="h-px bg-border/50 mx-6 lg:mx-auto lg:max-w-5xl" />
          <div className="py-8 px-4">
            <div className="max-w-2xl mx-auto text-sm text-muted-foreground space-y-2">
              <p className="leading-relaxed">
                Taken correctly, both the combined pill and the mini pill are over 99% effective at preventing pregnancy (around 91% with typical use), according to the NHS. Our pharmacists are specially trained in contraception and dedicated to this service, so the advice you get is tailored to you.
              </p>
              <p>Not sure which pill is right for you? <Link to="/guides/which-contraceptive-pill-is-right-for-me/" className="text-primary hover:underline font-medium">Read our guide</Link>.</p>
              <p>Starting the pill for the first time? <Link to="/guides/starting-the-pill/" className="text-primary hover:underline font-medium">Here's what to expect</Link>.</p>
              <p>Wondering about side effects? <Link to="/guides/side-effects-of-the-pill/" className="text-primary hover:underline font-medium">Learn more</Link>.</p>
              <Sources items={PILL_PAGE_SOURCES} />
            </div>
          </div>
          <PillFAQ />
          <PillFinalCTA />
          <Footer />
        </Suspense>
        <Suspense fallback={null}>
          <StartNowFab />
        </Suspense>
      </main>
    </>
  );
};

export default GetThePillOnline;
